import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslate, useNotify, useDataProvider, useRecordContext } from 'react-admin';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Typography,
    Autocomplete,
    TextField as MuiTextField,
    Chip,
    Alert,
    Link,
} from '@mui/material';
import { Groups } from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';

interface GroupOption {
    id: string;
    name: string;
    description?: string;
}

interface UserGroup {
    id: string;
    name: string;
    description?: string;
    addedAt?: string;
    addedBy?: string;
}

export const RfidUserGroupsField: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const record = useRecordContext();

    const [currentGroups, setCurrentGroups] = useState<GroupOption[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
    const [allGroups, setAllGroups] = useState<GroupOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const loadCurrentGroups = useCallback(async () => {
        if (!record?.id) return;
        setLoading(true);
        try {
            const resp: any = await apiClient.get(`/api/backend/app-rfid-users/${record.id}/groups`);
            const groups: GroupOption[] = (resp.data || []).map((g: UserGroup) => ({
                id: g.id,
                name: g.name,
                description: g.description,
            }));
            setCurrentGroups(groups);
            setSelectedGroups(groups);
        } catch {
            notify('ra.notification.data_provider_error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [record?.id, notify]);

    const loadAllGroups = useCallback(async () => {
        try {
            const resp = await dataProvider.getList('user-groups', {
                pagination: { page: 1, perPage: 500 },
                sort: { field: 'name', order: 'ASC' },
                filter: {},
            });
            setAllGroups(resp.data.map((g: any) => ({ id: g.id, name: g.name, description: g.description })));
        } catch {
            setAllGroups([]);
        }
    }, [dataProvider]);

    useEffect(() => {
        loadCurrentGroups();
        loadAllGroups();
    }, [loadCurrentGroups, loadAllGroups]);

    const handleChange = (_: React.SyntheticEvent, value: GroupOption[]) => {
        setSelectedGroups(value);
        setDirty(true);
    };

    const handleSave = async () => {
        if (!record?.id) return;
        setSaving(true);
        try {
            const currentIds = new Set(currentGroups.map(g => g.id));
            const selectedIds = new Set(selectedGroups.map(g => g.id));

            const toAdd = selectedGroups.filter(g => !currentIds.has(g.id));
            const toRemove = currentGroups.filter(g => !selectedIds.has(g.id));

            await Promise.all([
                ...toAdd.map(g =>
                    apiClient.post(`/api/backend/user-groups/${g.id}/members`, { rfidUserId: record.id })
                ),
                ...toRemove.map(g =>
                    apiClient.delete(`/api/backend/user-groups/${g.id}/members/rfid-user/${record.id}`)
                ),
            ]);

            notify('resources.rfid-users.groupsPanel.saved', { type: 'success' });
            setCurrentGroups(selectedGroups);
            setDirty(false);
        } catch {
            notify('ra.notification.data_provider_error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (!record?.id) return null;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Groups sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.rfid-users.groupsPanel.title')}
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {allGroups.length === 0 ? (
                            <Alert severity="info">
                                {translate('resources.rfid-users.groupsPanel.noGroupsExist')}{' '}
                                <Link component={RouterLink} to="/user-groups">
                                    {translate('resources.rfid-users.groupsPanel.manageGroups')}
                                </Link>
                            </Alert>
                        ) : (
                            <>
                                <Autocomplete
                                    multiple
                                    options={allGroups}
                                    value={selectedGroups}
                                    onChange={handleChange}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option.name}
                                                size="small"
                                                {...getTagProps({ index })}
                                                key={option.id}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <MuiTextField
                                            {...params}
                                            label={translate('resources.rfid-users.groupsPanel.title')}
                                            placeholder={selectedGroups.length === 0 ? translate('resources.rfid-users.groupsPanel.selectPlaceholder') : ''}
                                        />
                                    )}
                                    noOptionsText={translate('resources.rfid-users.groupsPanel.noOptions')}
                                />

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSave}
                                        disabled={!dirty || saving}
                                        sx={{
                                            backgroundColor: '#00C853',
                                            '&:hover': { backgroundColor: '#00A843' },
                                            '&:disabled': { backgroundColor: '#E0E0E0' },
                                        }}
                                    >
                                        {saving ? <CircularProgress size={16} /> : translate('resources.rfid-users.groupsPanel.save')}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default RfidUserGroupsField;
